import type { StringComparator } from "../Filters";

// The palette's input contract and the two assertions that guard it. Kept out
// of QuickSearch.tsx so that file exports only components (React Fast Refresh
// degrades when a component module also exports plain values), and so the
// assertions — which throw during render — can be unit-tested directly rather
// than through a story that would fail instead of asserting.

/**
 * A field quick search can target. Every property is free-text: the user
 * types a value and it commits a string filter. Which property a typed value
 * routes to is deterministic — a value matching a property's `pattern` (an id
 * shape like `/^ds_\S+$/`) goes to that property; anything else goes to the
 * `fallback`. There is no property-selection step, so these are routing
 * rules, not a menu.
 */
export interface QuickSearchProperty {
  /** Wire field the committed filter targets (e.g. `"dataset_id"`). */
  field: string;
  /** Display name shown in the quick result (e.g. `"ID"`). */
  name: string;
  /**
   * Recognizes values that belong to this property. A full shape, not just a
   * prefix: a dataset id is `ds_` followed by non-space characters
   * (`/^ds_\S+$/`), so `"ds_my dataset"` is correctly *not* an id and routes
   * to the fallback instead. Absent means the property is never
   * pattern-matched — it can still be the `fallback`.
   *
   * Must not carry the `g` flag — it makes `RegExp.test` stateful. Rejected
   * at runtime by {@link assertStatelessPatterns}.
   */
  pattern?: RegExp;
  /**
   * Marks this property the catch-all for a typed value no `pattern`
   * recognizes. **At most one** property may set it — violations throw, since
   * the type system cannot express the cardinality (see {@link
   * resolveFallback}). Omit it on every property to suppress the quick result
   * for unrecognized values.
   */
  fallback?: true;
  /** Comparator for the committed filter. Defaults to `"CONTAINS"`. */
  comparator?: StringComparator;
}

/**
 * Rejects `pattern`s carrying the `g` flag.
 *
 * `RegExp.test` advances `lastIndex` on a global regex, so the *same* value
 * alternates between matching and not on successive calls. Since the palette
 * tests each pattern once per render, a `/g` pattern would make an id
 * recognized on one render and unrecognized on the next — surfacing as a
 * quick result that flickers between properties. That reads like a React bug,
 * not a regex bug, which is why this is rejected outright rather than
 * documented: `g` is never correct for a yes/no shape check, so nothing
 * legitimate is lost.
 *
 * @throws if any property's pattern is global.
 */
export function assertStatelessPatterns(
  properties: QuickSearchProperty[],
): void {
  const global = properties.filter((p) => p.pattern?.global);
  if (global.length > 0) {
    throw new Error(
      `QuickSearch: pattern for ${global.map((p) => p.field).join(", ")} uses ` +
        `the \`g\` flag, which makes RegExp.test stateful. Remove it.`,
    );
  }
}

/**
 * The property a typed value routes to when no `pattern` recognizes it, or
 * `undefined` when the config declares none (a valid choice — it suppresses
 * the quick result for unrecognized values).
 *
 * Throws when more than one property claims the role. The cardinality isn't
 * expressible in the type system: a tuple-counting constraint was verified
 * against TS 5.5 and is silently bypassed by the `QuickSearchProperty[]`
 * annotation consumers write, so it would be inert exactly where it's
 * needed. Throwing is safe unconditionally because `properties` is a static
 * literal — which properties exist, and which claims `fallback`, are fixed
 * at authoring time and cannot vary by user, org, or environment. A bad
 * config therefore fails identically in dev, CI, and production, on the
 * palette's first render.
 *
 * **Call this unconditionally, on every render.** Guarding it behind the
 * quick-result branch (or a `useMemo` keyed on user input) would defer the
 * throw until someone happens to type an unrecognized value, turning a
 * build-time-obvious config error into a lurking runtime one. The cost is a
 * filter over a handful of static entries.
 *
 * @throws if two or more properties declare `fallback: true`.
 */
export function resolveFallback(
  properties: QuickSearchProperty[],
): QuickSearchProperty | undefined {
  const fallbacks = properties.filter((p) => p.fallback);
  if (fallbacks.length > 1) {
    throw new Error(
      `QuickSearch: ${fallbacks.length} properties declare \`fallback: true\` ` +
        `(${fallbacks.map((p) => p.field).join(", ")}). Exactly one may.`,
    );
  }
  return fallbacks[0];
}
